import { useState, useCallback, useEffect, useRef } from "react";
import { Button, Box, HStack } from "@chakra-ui/react";
import { FormattedMessage, useIntl } from "react-intl";
import { useFormikContext } from "formik";

import { useFlowConfig } from "flows/hooks";
import WalletBalance from "flows/PayoutV2/components/WalletBalance";
import PayoutAmountInput from "flows/PayoutV2/components/PayoutAmountInput";
import { FullPageSpinner } from "components/Spinner";
import EmptyState from "components/EmptyState";
import { useGetPrimaryWallet } from "flows/PayoutV2/hooks";
import { useCurrencyFormatter } from "components/FormattedMoney/useCurrencyFormatter";
import { useAnalytics } from "utils";

const PayoutAmount = ({ currencyToUse, onNextBtnClicked, txnLimits }) => {
  const analytics = useAnalytics();
  const viewEventFired = useRef(false);
  const [error, setError] = useState(null);
  const [nextBtnEnabled, setNextBtnEnabled] = useState(false);
  const [currency] = useState(currencyToUse);
  const { values, setFieldValue } = useFormikContext();
  const { formatMessage } = useIntl();
  const currencyFormatter = useCurrencyFormatter(currency);

  const { fixedAmount } = useFlowConfig();

  const {
    wallet,
    isLoading: isGetPrimaryWalletLoading,
    refetch: refetchPrimaryWallet,
    error: getPrimaryWalletError,
  } = useGetPrimaryWallet({ currency });

  useEffect(() => {
    if (wallet) {
      setFieldValue("sourcePaymentMethodId", wallet.id);
    }
  }, [wallet, setFieldValue]);

  useEffect(() => {
    if (!viewEventFired.current) {
      analytics.page("PayoutV2", "PayoutAmount_Viewed");
      viewEventFired.current = true;
    }
  }, [analytics, viewEventFired]);

  const validateAmountAgainstBalance = useCallback(() => {
    const limitOverrides = (txnLimits?.override || {})[currency];
    const walletBalance = (wallet && wallet.provider_data.balance) || 0;
    const amountRequested = values.amount || 0;
    const availableBalanceCoversAmount = amountRequested && walletBalance >= values.amount;
    const amountExceedsMax = limitOverrides?.max && amountRequested > limitOverrides?.max ? true : false;
    const amountBelowMin = limitOverrides?.min && amountRequested < limitOverrides?.min ? true : false;
    if (availableBalanceCoversAmount && !amountExceedsMax && !amountBelowMin) {
      setNextBtnEnabled(true);
      setError(null);
    } else {
      setNextBtnEnabled(false);
      let errMsg = null; // default message (can be the case if user enters '0')
      if (!availableBalanceCoversAmount && amountRequested !== 0) {
        errMsg = formatMessage({
          defaultMessage: "Insufficient balance",
          description: "Error message for insufficient balance",
        });
      } else if (amountExceedsMax) {
        errMsg = formatMessage(
          {
            defaultMessage: "{amount} Maximum",
            description: "Error message for payout amount exceeding maximum",
          },
          { amount: `${currencyFormatter(limitOverrides?.max)}` },
        );
      } else if (amountBelowMin) {
        errMsg = formatMessage(
          {
            defaultMessage: "{amount} Minimum",
            description: "Error message for payout amount being less than minimum",
          },
          { amount: `${currencyFormatter(limitOverrides?.min)}` },
        );
      }
      setError(errMsg);
    }
  }, [
    values.amount,
    wallet,
    setError,
    setNextBtnEnabled,
    formatMessage,
    txnLimits,
    currency,
    currencyFormatter,
  ]);

  useEffect(() => {
    validateAmountAgainstBalance(); // this function is a useCallback which will monitor for the changes it cares about
  }, [validateAmountAgainstBalance]);

  const handleNextBtnClicked = useCallback(() => {
    if (values.amount && onNextBtnClicked) {
      onNextBtnClicked();
    }
  }, [values, onNextBtnClicked]);

  if (isGetPrimaryWalletLoading) {
    return <FullPageSpinner data-testid="payout-method-selector" />;
  }

  if (getPrimaryWalletError) {
    return <EmptyState error={getPrimaryWalletError} />;
  }

  return (
    <Box background="lightBackground" position="relative">
      <Box px="7" py="6" zIndex="1" position="relative">
        <WalletBalance onUpdate={refetchPrimaryWallet} />
      </Box>
      <Box
        px="7"
        pb="6"
        pt={"5"}
        backgroundColor="background"
        borderTopRadius="40px"
        transition="all 0.5s"
      >
        <PayoutAmountInput currency={currency} isDisabled={!!fixedAmount} error={error} />
        <HStack justify="flex-end">
          <Button
            isLoading={false}
            size="lg"
            onClick={handleNextBtnClicked}
            isDisabled={!nextBtnEnabled}
          >
            <FormattedMessage defaultMessage="Next" description="Button label" />
          </Button>
        </HStack>
      </Box>
    </Box>
  );
};

export default PayoutAmount;
